import { attributes } from '../../core/ids-attributes';
import { IdsBaseConstructor } from '../../core/ids-element';

export interface BoxModelSpacingHandler {
  /** Override to provide a custom target element for spacing styles */
  get spacingTarget(): HTMLElement | SVGElement | null | undefined;
}

/**
 * Adds padding and margin attributes that apply inline styles to a target element.
 * Components can override `get spacingTarget()` to control which element receives the styles.
 * Defaults to `this.container` or `this.rootEl`.
 * @param {T} superclass Accepts a superclass and creates a new subclass from it
 * @returns {T} The extended object
 */
const IdsBoxModelSpacingMixin = <T extends IdsBaseConstructor>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  connectedCallback(): void {
    super.connectedCallback?.();
    this.#applyInitialSpacing();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
  }

  static get attributes(): string[] {
    return [
      ...(superclass as any).attributes,
      attributes.PADDING,
      attributes.PADDING_X,
      attributes.PADDING_Y,
      attributes.MARGIN_Y,
    ];
  }

  /**
   * Default spacing target — components can override this getter
   * @returns {HTMLElement | SVGElement | null | undefined} The target element
   */
  get spacingTarget(): HTMLElement | SVGElement | null | undefined {
    return (this as any).container || (this as any).rootEl;
  }

  /**
   * Apply initial spacing from attributes on connect
   */
  #applyInitialSpacing() {
    if (this.hasAttribute(attributes.PADDING)) {
      const value = this.getAttribute(attributes.PADDING);
      if (value) this.padding = value;
    }
    if (this.hasAttribute(attributes.PADDING_X)) {
      const value = this.getAttribute(attributes.PADDING_X);
      if (value) this.paddingX = value;
    }
    if (this.hasAttribute(attributes.PADDING_Y)) {
      const value = this.getAttribute(attributes.PADDING_Y);
      if (value) this.paddingY = value;
    }
    if (this.hasAttribute(attributes.MARGIN_Y)) {
      const value = this.getAttribute(attributes.MARGIN_Y);
      if (value) this.marginY = value;
    }
  }

  /**
   * Set the padding value (in pixels), applies to all sides
   * @param {string | number | null} value The padding value
   */
  set padding(value: string | number | null) {
    if (value !== null && value !== undefined && value !== '') {
      this.setAttribute(attributes.PADDING, value.toString());
      this.spacingTarget?.style.setProperty('padding', `${value}px`);
    } else {
      this.removeAttribute(attributes.PADDING);
      this.spacingTarget?.style.removeProperty('padding');
    }
  }

  get padding(): string | null { return this.getAttribute(attributes.PADDING); }

  /**
   * Set the padding-x value (in pixels), applies to inline start and end
   * @param {string | number | null} value The padding-x value
   */
  set paddingX(value: string | number | null) {
    if (value !== null && value !== undefined && value !== '') {
      this.setAttribute(attributes.PADDING_X, value.toString());
      this.spacingTarget?.style.setProperty('padding-inline', `${value}px`);
    } else {
      this.removeAttribute(attributes.PADDING_X);
      this.spacingTarget?.style.removeProperty('padding-inline');
    }
  }

  get paddingX(): string | null { return this.getAttribute(attributes.PADDING_X); }

  /**
   * Set the padding-y value (in pixels), applies to block start and end
   * @param {string | number | null} value The padding-y value
   */
  set paddingY(value: string | number | null) {
    if (value !== null && value !== undefined && value !== '') {
      this.setAttribute(attributes.PADDING_Y, value.toString());
      this.spacingTarget?.style.setProperty('padding-block', `${value}px`);
    } else {
      this.removeAttribute(attributes.PADDING_Y);
      this.spacingTarget?.style.removeProperty('padding-block');
    }
  }

  get paddingY(): string | null { return this.getAttribute(attributes.PADDING_Y); }

  /**
   * Set the margin-y value (in pixels), applies to block start and end
   * @param {string | number | null} value The margin-y value
   */
  set marginY(value: string | number | null) {
    if (value !== null && value !== undefined && value !== '') {
      this.setAttribute(attributes.MARGIN_Y, value.toString());
      this.spacingTarget?.style.setProperty('margin-block', `${value}px`);
    } else {
      this.removeAttribute(attributes.MARGIN_Y);
      this.spacingTarget?.style.removeProperty('margin-block');
    }
  }

  get marginY(): string | null { return this.getAttribute(attributes.MARGIN_Y); }
};

export default IdsBoxModelSpacingMixin;
